/**
 * notifications/describe.ts — one dropdown LINE per notification, for every kind the backend can
 * write. Pure: no JSX, no fetch, no router.
 *
 * WHY IT IS ITS OWN MODULE. It lived in NotificationBell.tsx, whose header notes that this terse
 * dropdown wording and the /notifications page's fuller `rowFor` are deliberately separate — and that
 * the two DID drift, both going blind to kind 7's `subjectKind: 'report'` and telling someone who
 * RAISED a report that their own post had been moderated. The Kombi header now needs the same lines,
 * and a third copy is how that happens again. So the mapping moved here and both bells import it.
 * NotificationBell re-exports it, so its existing test and every previous importer are unaffected.
 *
 * The page's `rowFor` is still a separate implementation. Converging all three is a real change with
 * a product-copy decision in it (the page carries kind 7's reason and NT-F17's respond link, the
 * dropdown deliberately does not), so it is not folded in here.
 */
import type { NotificationEntry } from './api';

/**
 * THE DESTINATIONS THAT ARE NOT A `/:id` PATH, shared with the page's `rowFor` rather than written
 * twice — because this is the half of "keep the two in step" that had no test and drifted anyway.
 *
 * EVERY ONE IS A KOMBI ROUTE (kombi/productionRoutes.ts, router.tsx's top-level lazy routes), and that
 * is the whole point of naming them here. A notification is the platform tapping someone on the
 * shoulder, so it must land them on the interface the platform now IS. Three of these used to point
 * into the legacy `<App/>` frame — `/notifications`, `/tools` — and the ids the other arms build
 * (`/tools/:toolId`, `/me`, `/community`, `/`) are already live Kombi routes, so those three were the
 * only ways a notification could still open the old interface. They are written as literals, matching
 * the rest of this module, but each one is a `kombiProductionRoutes` value and must stay one: check
 * that map before changing a path here, and change the pair of them together.
 */
export const NOTIFICATION_DESTINATIONS = {
  /** `/admin`'s Review queue panel, opened on arrival — the page an awaiting-review row exists to reach. */
  reviewQueue: '/admin?panel=review',
  /** `/admin` itself, which opens on the needs-attention summary (PC-F16) — every queue is one click on. */
  admin: '/admin',
  /**
   * `kombiProductionRoutes.toolCatalogue`. NOT `/tools`: the redesign has category landing routes and
   * `/tools` is the LEGACY catalogue inside the old frame (router.tsx), so a reporter's row about a
   * tool comment used to be the one kind-7 destination that left Kombi.
   */
  toolCatalogue: '/catalogue/automations',
  /**
   * Where a kind nobody has wired yet lands. It was `/notifications` — the legacy feed page, which the
   * Kombi header has SHELVED (kombi/Header.tsx: "the whole feed lives in this menu"), so the generic
   * row was a one-way door out of the new interface and into the old one. `/me` is the signed-in
   * person's own Kombi surface: the honest place to put someone when all we know is that something
   * concerning them happened.
   */
  unknownKind: '/me',
} as const;

/**
 * One dropdown line per notification, for EVERY kind the backend can write (3, 4, 5, 6, 7, 8 — kind 1
 * goes to the community channel and never lands in a personal feed). Non-nullable on purpose: the
 * `unreadCount` beside these rows is counted in SQL and cannot know what this function refuses to
 * draw, so a `null` return here is the bell showing a count over an empty panel. It did exactly that
 * for kinds 4, 7 and 8, which the backend has been writing all along.
 *
 * A NON-NULL RETURN IS NOT THE SAME AS A USEFUL ONE, which is the lesson kind 5 added on 2026-09-07.
 * The generic arm at the bottom kept the row visible and counted, exactly as designed — and because it
 * did, a kind that had been fanning out to every Reviewer since 2026-09-02 read as "You have a new
 * notification" for five days without looking broken. The list of arms here is only as current as
 * db/notification.ts's WIRED KINDS list; check it, don't infer it from this file.
 *
 * Deliberately TERSER than the /notifications page's `rowFor`, which is why the two are not shared:
 * a dropdown line is a pointer, so kind 7's reason — and NT-F17's respond-to-it link beneath it — are
 * delivered on the page this links to, not here. Keep the two in step: a kind added to one belongs in
 * the other, and so does a kind-7 `subjectKind`. The wording of each line must match, because these are
 * the same fact seen twice and a bell that says something else is the more visible of the two.
 *
 * Kind 6's, and kind 7's author-limb, href carries ?highlight=<postId> so the thread page can scroll to
 * and flash the exact post being pointed at (a one-time visual cue on the THREAD, not on this list —
 * NT-F06/F07: still no name/body here, just where to look once you get there). The reporter limb has no
 * post id to highlight and is not given one.
 *
 * EXPORTED ONLY SO IT CAN BE TESTED (test/notificationRows.test.ts), alongside the page's `rowFor`, in
 * the same file and against the same payloads — because "keep the two in step" is an instruction no
 * typechecker enforces, and the two DID drift: the page and the bell were separately blind to
 * `subjectKind: 'report'`. Nothing else imports it.
 */
export function describe(n: NotificationEntry): { text: string; href: string } {
  // Ids and governed enums only, read through one defaulted local so a null/scalar payload cannot
  // throw and blank the whole panel. Kind 7's `context` is spread FLAT onto the payload server-side —
  // there is no nested `context` key, and reading one is how every kind-7 href once came out bare.
  const p = (typeof n.payload === 'object' && n.payload !== null ? n.payload : {}) as {
    toolId?: string;
    issueId?: string;
    threadId?: string;
    postId?: string;
    subjectKind?: string;
    status?: string;
    // Kind 7's reporter limb only: what was reported, as a governed enum and its row id. `targetId`
    // is unused in any href on purpose — see the 'report' arm, and rowFor's fuller note on why.
    targetKind?: string;
    targetId?: string;
    // Kind 5 only: which needs-attention limb fired, and the submitted tool's own name.
    reason?: string;
    name?: string;
  };
  const toolHref = typeof p.toolId === 'string' ? `/tools/${encodeURIComponent(p.toolId)}` : '/me';
  const threadHref =
    typeof p.threadId === 'string'
      ? `/community/thread/${encodeURIComponent(p.threadId)}` +
        (typeof p.postId === 'string' ? `?highlight=${encodeURIComponent(p.postId)}` : '')
      : '/community';
  const status = typeof p.status === 'string' ? p.status : null;

  if (n.kind === 3) {
    return {
      text: 'issueId' in p ? 'New issue report on your tool' : 'New comment on your tool',
      href: toolHref,
    };
  }
  if (n.kind === 4) {
    // `status` is the governed PublicationStatus, i.e. the catalogue's own word (UP-F36).
    return {
      text:
        status === null
          ? 'Your tool submission changed status'
          : status === 'Approved' || status === 'Rejected' || status === 'Withdrawn'
            ? `Your tool submission was ${status.toLowerCase()}`
            : `Your tool submission is now ${status}`,
      href: toolHref,
    };
  }
  if (n.kind === 5) {
    // WIRED 2026-09-02 AND UNRENDERED UNTIL NOW. `notifySubmissionAwaitingReview` fans kind 5 out to
    // every Reviewer, and neither this nor the page's `rowFor` knew the kind — so a reviewer was told
    // "You have a new notification" and sent to the shelved legacy /notifications page. Exactly the
    // drift this module's header describes, one kind later.
    //
    // Switched on `reason`, not assumed: kind 5 is the operational needs-attention CHANNEL and NT §7
    // gives it four more limbs (unmaintained/broken/ownerless tools, flagged content, an overdue support
    // request) that are still unbuilt. Reading any of those as a submission would be a confident lie, so
    // an unrecognised reason says only what is certain and lands on /admin, where all five limbs are
    // one click apart.
    const name = typeof p.name === 'string' && p.name !== '' ? p.name : null;
    if (p.reason === 'submission_awaiting_review') {
      return {
        // `name` is the tool's own name, sent by the writer precisely "so the feed can say WHICH
        // submission without a second read" — a submitted automation, not a colleague, so NT-F06/F07's
        // no-third-party rule is not in play here.
        text:
          name === null
            ? 'A submission is waiting for your review'
            : `${name} is waiting for your review`,
        href: NOTIFICATION_DESTINATIONS.reviewQueue,
      };
    }
    return { text: 'Something needs your attention', href: NOTIFICATION_DESTINATIONS.admin };
  }
  if (n.kind === 6) {
    return { text: 'New reply to your post', href: threadHref };
  }
  if (n.kind === 7) {
    // The REPORTER's limb first (CH-F14 / FB-F17), the one kind-7 row written to somebody who was not
    // judged: `{subjectKind: 'report', subjectId, status, targetKind, targetId}`, no reason and no
    // context ids. It used to fall through to "A decision was made about your content", telling the
    // person who RAISED a report that their own post had been moderated. `targetKind` names what was
    // reported; `targetId` is a post/comment id that no route accepts, so the href is the surface, not
    // the item (the page's `rowFor` carries the long version of that reasoning).
    if (p.subjectKind === 'report') {
      const reported =
        p.targetKind === 'forum_post'
          ? 'post'
          : p.targetKind === 'comment'
            ? 'tool comment'
            : 'content';
      return {
        text:
          status === 'dismiss'
            ? `The ${reported} you reported was reviewed and kept`
            : status === 'remove'
              ? `The ${reported} you reported was removed`
              : `A decision was made on the ${reported} you reported`,
        href: p.targetKind === 'comment' ? NOTIFICATION_DESTINATIONS.toolCatalogue : '/community',
      };
    }
    // A flag on your OWN content sits on a forum post OR a tool comment and `subjectKind` does not say
    // which, so the noun comes from which context id arrived with the row.
    const flagged = typeof p.threadId === 'string' ? 'post' : 'comment';
    const text =
      p.subjectKind === 'request'
        ? status === null
          ? 'Your support request was updated'
          : `Your support request is now ${status}`
        : p.subjectKind === 'comment'
          ? 'Your comment on a tool was removed'
          : p.subjectKind === 'flag'
            ? status === 'dismiss'
              ? `Your ${flagged} was reported, reviewed and kept`
              : status === 'remove'
                ? `Your ${flagged} was removed after a report`
                : `A moderator decided the report on your ${flagged}`
            : 'A decision was made about your content';
    return {
      text,
      // A support request has no thread and often no tool, so it lands on /community, where "Your
      // support requests" lists it with the reason for every status it has been through.
      href:
        typeof p.threadId === 'string'
          ? threadHref
          : typeof p.toolId === 'string'
            ? toolHref
            : '/community',
    };
  }
  if (n.kind === 8) {
    // Once per tool ever, on FIRST entry (RT-F21) — "reached" stays true after it drops out again.
    return {
      text: 'Your tool reached the weekly Top 5',
      href: typeof p.toolId === 'string' ? toolHref : '/',
    };
  }
  // Kind 2 is unbuilt and nothing else may exist (NT-F02, NT §8's three banned kinds), so this is
  // unreachable today — and if it stops being, the row is still visible and still counted. Kind 5 used
  // to be here on that same reasoning and had been WRITING for five days; this arm is the safety net,
  // not a place for a wired kind to sit, so read the WIRED KINDS list in db/notification.ts before
  // trusting that the arms above are complete.
  return { text: 'You have a new notification', href: NOTIFICATION_DESTINATIONS.unknownKind };
}
